import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Button, Card, Chip, ConfirmDialog, EmptyState, ErrorNotice, Field, KV, Notice, PageHeader, Select, Skeleton, Table, TabPanel, Tabs } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { api } from '@/lib/api';
import { errorText } from '@/lib/errors';
import { phoneDisplay } from '@/lib/format';
import type { Signup } from '@/lib/types';
import { useRegions } from './common';

type Tab = 'pending' | 'approved' | 'rejected';

export function AdminSignups() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('pending');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [regionId, setRegionId] = useState('');
  const [reject, setReject] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning' | 'error'; text: string; userId?: string } | null>(null);
  const list = useQuery({ queryKey: ['admin', 'signups', tab], queryFn: () => api.get<{ items: Signup[] }>(`/api/admin/signup-requests?status=${tab}`) });
  const counts = useQuery({ queryKey: ['admin', 'signups', 'counts'], queryFn: async () => ({ pending: (await api.get<{ items: Signup[] }>('/api/admin/signup-requests?status=pending')).items.length }) });
  const regions = useRegions();
  const items = list.data?.items ?? [];
  const selected = items.find((s) => s.id === selectedId) ?? items[0] ?? null;
  useEffect(() => { setRegionId(''); setReject(false); }, [selected?.id]);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['admin'] }); setSelectedId(null); };
  const approve = useMutation({
    mutationFn: () => api.post<{ id: string; email: string; inviteEmailSent: boolean }>(`/api/admin/signup-requests/${selected!.id}/approve`, { regionId: regionId || undefined }),
    onSuccess: (r) => { setNotice(r.inviteEmailSent ? { tone: 'success', text: t('admin.signups.approved', { email: r.email }), userId: r.id } : { tone: 'warning', text: t('admin.signups.approvedNoEmail'), userId: r.id }); refresh(); },
    onError: (e) => setNotice({ tone: 'error', text: errorText(e, t) }),
  });
  const rejectM = useMutation({ mutationFn: (reason: string) => api.post(`/api/admin/signup-requests/${selected!.id}/reject`, { reason }), onSuccess: () => { setReject(false); refresh(); } });
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.signups') }]} title={t('nav.signups')} />
      {notice ? <Notice tone={notice.tone}>{notice.text}{notice.userId ? <span> · <Link to={`/admin/users/${notice.userId}`}>{t('admin.signups.openAccount')}</Link></span> : null}</Notice> : null}
      <Tabs<Tab> id="admin-signups" value={tab} onChange={(k) => { setTab(k); setSelectedId(null); }} items={[{ key: 'pending', label: t('admin.signups.tabs.pending'), count: counts.data?.pending }, { key: 'approved', label: t('admin.signups.tabs.approved') }, { key: 'rejected', label: t('admin.signups.tabs.rejected') }]} />
      <TabPanel id="admin-signups" value={tab}>
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
          {list.isLoading ? <Skeleton lines={6} /> : list.error ? <ErrorNotice error={list.error} retry={() => void list.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : items.length ? (
            <Card className="p-0 overflow-hidden">
              <Table className="border-0 rounded-none">
                <thead><tr><th>{t('app.kind')}</th><th>{t('app.name')}</th><th>{t('admin.users.crNumber')}</th><th>{t('admin.users.licenceNo')}</th><th>{t('admin.users.licenceExpiry')}</th><th>{t('app.phone')}</th><th>{t('admin.signups.submitted')}</th></tr></thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id} className={selected?.id === s.id ? 'bg-bg-2 cursor-pointer' : 'cursor-pointer'} onClick={() => setSelectedId(s.id)}>
                      <td><Chip form="mint">{t(`roles.${s.kind}`)}</Chip></td>
                      <td>{s.name}</td>
                      <td className="num">{s.crNumber}</td>
                      <td className="num">{s.licenceNo}</td>
                      <td className="num">{s.licenceExpiry}</td>
                      <td className="ltr num">{phoneDisplay(s.phone)}</td>
                      <td className="text-muted text-[13px]">{formatRelative(s.createdAt, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          ) : <EmptyState title={t('app.none')} />}
          <aside className="rounded-sm border border-line-2 bg-bg p-6 lg:sticky lg:top-6 flex flex-col gap-4">
            {!selected ? <p className="text-muted text-[14px]">{t('admin.signups.pickOne')}</p> : (
              <>
                <div>
                  <p className="label">{t('admin.signups.joinRequest', { kind: t(`roles.${selected.kind}`) })}</p>
                  <h2 className="mt-1">{selected.name}</h2>
                </div>
                <KV items={[
                  { k: t('admin.users.crNumber'), v: selected.crNumber, num: true },
                  { k: t('admin.users.licenceNo'), v: <span><span className="num">{selected.licenceNo}</span> · {t('admin.users.licenceExpiry')} <span className="num">{selected.licenceExpiry}</span></span> },
                  { k: t('app.phone'), v: <span className="ltr">{phoneDisplay(selected.phone)}</span> },
                  { k: t('app.email'), v: <span className="ltr">{selected.email}</span> },
                ]} />
                <div>
                  <p className="label mb-2">{t('admin.signups.document')}</p>
                  {selected.documentUrl && !selected.documentUrl.startsWith('mock://') ? <a href={selected.documentUrl} target="_blank" rel="noreferrer" className="text-[14px]">{t('admin.signups.openDocument')}</a> : <p className="text-muted text-[14px]">{selected.documentMediaId ? t('admin.signups.openDocument') : t('admin.signups.noDocument')}</p>}
                </div>
                {selected.status === 'pending' ? (
                  <>
                    <Notice tone="info">{t('admin.signups.verifyHint')}</Notice>
                    <Field label={t('admin.signups.region')} htmlFor="su-region">
                      <Select id="su-region" value={regionId} onChange={(e) => setRegionId(e.target.value)}>
                        <option value="">—</option>
                        {regions.data?.items.map((r) => <option key={r.id} value={r.id}>{locale === 'ar' ? r.nameAr : r.nameEn}</option>)}
                      </Select>
                    </Field>
                    <div className="flex gap-2">
                      <Button variant="primary" className="flex-1" loading={approve.isPending} onClick={() => approve.mutate()}>{t('admin.signups.approve')}</Button>
                      <Button variant="danger" onClick={() => setReject(true)}>{t('admin.signups.reject')}</Button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted text-[14px]">
                    {t('admin.signups.reviewed', { when: selected.reviewedAt ? formatRelative(selected.reviewedAt, locale) : '' })}
                    {selected.rejectReason ? ` · ${selected.rejectReason}` : ''}
                    {selected.createdUserId ? <span> · <Link to={`/admin/users/${selected.createdUserId}`}>{t('admin.signups.openAccount')}</Link></span> : null}
                  </p>
                )}
              </>
            )}
          </aside>
        </div>
      </TabPanel>
      <ConfirmDialog open={reject} onClose={() => setReject(false)} title={t('admin.signups.rejectTitle', { name: selected?.name ?? '' })} confirmLabel={t('admin.signups.reject')} cancelLabel={t('app.back')} danger reason={{ label: t('admin.signups.rejectReason'), required: true }} loading={rejectM.isPending} onConfirm={(r) => rejectM.mutate(r)} />
    </div>
  );
}
